/**
 * S1b-b 글쓰기 → 상회칠판 고민 칸의 「고민 올리기」 (시안 05 · 09, 08 6.4 · 결정 36). 전체 화면, 아래에서 올라옴 240ms.
 *   상단 바(닫기 × · 「고민 올리기」 · 「올리기」) → 안내 → 입력 상자(최소 240, 1.5px --brand, 17/28, 1000자, 「{n} / 1000」) → 고지 상자
 * - 종류는 고민으로 고정(일상 선택 없음). 일상 얘기는 별채대화에서
 * - initialText: 별채대화의 「고민 카드로 올려 둘까요?」 칩에서 오면 단골이 한 말을 먼저 채웁니다
 * - 닫을 때 쓴 게 있으면 「쓰던 글을 지울까요?」
 * - 올리기 전 위기 분류(서버). L1 이상이면 올리지 않고 안전 카드
 * - 올리면 부르는 쪽이 글 상세로 보냅니다
 */
import { useState } from 'react';
import { X } from 'lucide-react';
import { SafetyCard } from '../../components/SafetyCard.jsx';
import { errorMessageOf } from '../../api/client.js';
import * as boardApi from '../../api/board.js';

const MAX_LENGTH = 1000;
const KIND = 'worry';

function newClientKey() {
    return crypto.randomUUID();
}

export function PostComposer({ open, onClose, onPosted, initialText = '' }) {
    const [text, setText] = useState(initialText);
    const [seededWith, setSeededWith] = useState(initialText);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const [safety, setSafety] = useState(null);
    const [confirmClose, setConfirmClose] = useState(false);
    const [clientKey, setClientKey] = useState(newClientKey);

    // 다른 글로 다시 열리면 그 글을 채웁니다
    if (open && initialText !== seededWith) {
        setSeededWith(initialText);
        setText(initialText);
    }

    if (!open) {
        return null;
    }

    function reset() {
        setText('');
        setError(null);
        setSafety(null);
        setConfirmClose(false);
        setClientKey(newClientKey());
    }

    function requestClose() {
        if (text.trim() && !confirmClose) {
            setConfirmClose(true);
            return;
        }
        reset();
        onClose();
    }

    async function submit() {
        if (!text.trim() || busy) {
            return;
        }
        setBusy(true);
        setError(null);
        try {
            const { post } = await boardApi.createPost({ kind: KIND, body: text.trim(), clientKey });
            reset();
            onPosted(post);
        } catch (requestError) {
            if (requestError?.code === 'CRISIS_POST') {
                setSafety(requestError.details?.safetyLevel === 'L3' ? 'L3' : 'L2');
            }
            setError(errorMessageOf(requestError, '글을 올리지 못했어요. 잠시 후 다시 시도해 주세요.'));
        } finally {
            setBusy(false);
        }
    }

    const length = Array.from(text).length;
    return (
        <div
            role="dialog"
            aria-modal="true"
            aria-label="고민 올리기"
            className="fixed inset-0 z-50 mx-auto flex max-w-app flex-col bg-bg pt-[env(safe-area-inset-top,0px)] pb-[env(safe-area-inset-bottom,0px)] motion-safe:animate-sheet-up"
        >
            <header className="flex h-bar shrink-0 items-center gap-1 border-b border-hairline px-1">
                <button
                    type="button"
                    aria-label="닫기"
                    onClick={requestClose}
                    className="flex size-touch items-center justify-center"
                >
                    <X size={22} strokeWidth={1.6} aria-hidden="true" />
                </button>
                <h1 className="m-0 text-[17px] font-semibold">고민 올리기</h1>
                <button
                    type="button"
                    onClick={submit}
                    disabled={!text.trim() || busy}
                    className="mr-2 ml-auto h-9 rounded-[18px] bg-brand px-4 text-[14px] font-semibold text-on-brand disabled:opacity-40"
                >
                    올리기
                </button>
            </header>
            <main className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto p-5">
                <p className="m-0 text-[12px] leading-[18px] text-text-secondary">
                    멤버 두 명이 서로 다른 쪽에서 답해요. 그냥 수다는 별채에서 해요.
                </p>
                <div className="relative">
                    <textarea
                        value={text}
                        onChange={(event) => {
                            setText(event.target.value);
                            setConfirmClose(false);
                        }}
                        maxLength={MAX_LENGTH}
                        aria-label="글 내용"
                        placeholder="어떤 고민이에요?"
                        className="min-h-60 w-full resize-none rounded-card border-[1.5px] border-brand bg-surface p-4 pb-8 text-[17px] leading-[28px]"
                    />
                    <span className="absolute right-4 bottom-3 text-[12px] text-text-secondary">
                        {length} / {MAX_LENGTH}
                    </span>
                </div>
                <p className="m-0 rounded-[12px] bg-surface-sunken p-3 text-[12px] leading-[18px] text-text-secondary">
                    상회칠판에 올린 글은 멤버들이 모두 봐요. 다른 사람 이름 · 연락처는 적지 않는 게 좋아요.
                </p>
                {safety ? <SafetyCard level={safety} /> : null}
                {error ? (
                    <p role="alert" className="m-0 text-caption text-error">
                        {error}
                    </p>
                ) : null}
                {confirmClose ? (
                    <div role="alert" className="flex items-center gap-3 rounded-card bg-surface p-3">
                        <span className="flex-1 text-[14px]">쓰던 글을 지울까요?</span>
                        <button type="button" onClick={requestClose} className="text-[14px] font-semibold text-error">
                            지우고 닫기
                        </button>
                        <button
                            type="button"
                            onClick={() => setConfirmClose(false)}
                            className="text-[14px] font-semibold text-text-secondary"
                        >
                            계속 쓰기
                        </button>
                    </div>
                ) : null}
            </main>
        </div>
    );
}
